<script setup>
// [xleaders] XO-27: ô cảm xúc của SALE trên tin khách (Zalo cá nhân). Là flex sibling của bong bóng
// trong Message.vue (không nằm trong Base.vue vì bubble ảnh/video/file `overflow-hidden`), tách khỏi
// badge XO-12 (icon của KHÁCH, trong bong bóng) để hai icon không đè nhau. Có icon đã thả thì luôn
// hiện icon đó; chưa có thì hiện `smile-plus` khi rê chuột lên hàng tin (`group/zalo` ở Message.vue).
// Bấm → bảng 6 icon thanh nhanh của Zalo + nút gỡ. Không cập nhật lạc quan: Rails ghi
// `zalo_self_reaction` xong thì `message.updated` về qua ActionCable và ô đổi theo — cùng đường với
// reaction host thả từ điện thoại.
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import zaloReactionAPI from 'dashboard/api/xleaders/zaloReaction';
import { useMessageContext } from '../provider.js';

const props = defineProps({
  current: { type: String, default: '' },
});

// Cùng mã với `ZaloPersonal::SendReactionService::ICONS` và bảng `ReactionService::EMOJI`.
const REACTIONS = [
  { code: '/-heart', emoji: '❤️' },
  { code: '/-strong', emoji: '👍' },
  { code: ':>', emoji: '😆' },
  { code: ':o', emoji: '😮' },
  { code: ':-((', emoji: '😢' },
  { code: ':-h', emoji: '😠' },
];

const { id, conversationId } = useMessageContext();
const route = useRoute();
const { t } = useI18n();

const open = ref(false);
const sending = ref(false);
const rootRef = ref(null);
onClickOutside(rootRef, () => {
  open.value = false;
});

const ERROR_KEYS = {
  reaction_target_unknown: 'CONVERSATION.ZALO_REACTION.ERROR_TARGET_UNKNOWN',
};

const send = async code => {
  open.value = false;
  sending.value = true;
  try {
    await zaloReactionAPI.update(
      route.params.accountId,
      conversationId.value,
      id.value,
      code
    );
  } catch (error) {
    const key =
      ERROR_KEYS[error?.response?.data?.error] ||
      'CONVERSATION.ZALO_REACTION.ERROR';
    useAlert(t(key));
  } finally {
    sending.value = false;
  }
};
</script>

<template>
  <div
    ref="rootRef"
    class="relative self-end shrink-0 ltr:ml-1 rtl:mr-1"
    @click.stop
    @keydown.esc="open = false"
  >
    <button
      type="button"
      data-test-id="zalo-reaction-trigger"
      :title="t('CONVERSATION.ZALO_REACTION.ADD')"
      class="flex items-center justify-center size-6 p-0 text-xs leading-none rounded-full bg-n-background outline outline-1 outline-n-weak shadow-sm transition-opacity focus:opacity-100"
      :class="
        props.current || open
          ? 'opacity-100'
          : 'opacity-0 group-hover/zalo:opacity-100'
      "
      :disabled="sending"
      @click="open = !open"
    >
      <span v-if="props.current">{{ props.current }}</span>
      <span
        v-else
        class="i-lucide-smile-plus size-4 shrink-0 text-n-slate-11"
      />
    </button>
    <div
      v-if="open"
      data-test-id="zalo-reaction-panel"
      class="absolute z-10 flex gap-1 p-1 mb-1 bottom-full ltr:right-0 rtl:left-0 rounded-full bg-n-solid-1 outline outline-1 outline-n-weak shadow-md"
    >
      <button
        v-for="reaction in REACTIONS"
        :key="reaction.code"
        type="button"
        class="flex items-center justify-center size-7 p-0 text-base leading-none rounded-full hover:bg-n-alpha-2"
        :class="{ 'bg-n-alpha-2': props.current === reaction.emoji }"
        @click="send(reaction.code)"
      >
        {{ reaction.emoji }}
      </button>
      <button
        type="button"
        data-test-id="zalo-reaction-remove"
        :title="t('CONVERSATION.ZALO_REACTION.REMOVE')"
        class="flex items-center justify-center size-7 p-0 rounded-full hover:bg-n-alpha-2"
        @click="send('')"
      >
        <span class="i-lucide-heart-off size-4 shrink-0 text-n-slate-11" />
      </button>
    </div>
  </div>
</template>
